import React from "react";
import styled, { keyframes } from "styled-components";

interface ChatMessageProps {
    direction: "inbound" | "outbound";
    body: string;
    isActivio: boolean;
    sentAt: string;
}

const slideIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
`;

const Row = styled.div<{ $isInbound: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    justify-content: ${(props) => (props.$isInbound ? "flex-end" : "flex-start")};
    margin-bottom: 6px;
    animation: ${slideIn} 0.25s ease-out;
`;

const Avatar = styled.div`
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2432b1 0%, #1a3299 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
`;

const BubbleColumn = styled.div<{ $isInbound: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: ${(props) => (props.$isInbound ? "flex-end" : "flex-start")};
    max-width: 80%;
`;

const Bubble = styled.div<{ $isInbound: boolean }>`
    padding: 12px 16px;
    font-size: 14px;
    line-height: 1.5;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-word;
    white-space: pre-wrap;

    a {
        color: inherit;
        text-decoration: underline;
    }

    ${(props) =>
        props.$isInbound
            ? `
        background: #2432b1;
        color: white;
        border-radius: 16px;
        border-bottom-right-radius: 4px;
    `
            : `
        background: white;
        color: #1e293b;
        border-radius: 16px;
        border-top-left-radius: 4px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    `}
`;

const Time = styled.span`
    font-size: 11px;
    color: #94a3b8;
    margin-top: 4px;
    padding: 0 4px;
`;

/** Convert **bold**, *bold* and URLs into React elements. */
function formatBody(text: string): React.ReactNode[] {
    // Split by bold markers (**text** or *text*) and URLs
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|https?:\/\/\S+)/g);
    return parts.map((part, i) => {
        // **bold** → <strong>
        if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        // *bold* → <strong>
        if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
            return <strong key={i}>{part.slice(1, -1)}</strong>;
        }
        // URL → clickable link (strip trailing punctuation from the link)
        if (/^https?:\/\//.test(part)) {
            const match = part.match(/^(https?:\/\/[^\s,;:.!?)]+(?:\.[^\s,;:.!?)]+)*(?:\/[^\s,;:.!?)\]]*)*)(.*)/);
            if (match) {
                const [, url, trailing] = match;
                return (
                    <React.Fragment key={i}>
                        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                        {trailing}
                    </React.Fragment>
                );
            }
            return <a key={i} href={part} target="_blank" rel="noopener noreferrer">{part}</a>;
        }
        return part;
    });
}

export default function ChatMessage({
    direction,
    body,
    isActivio,
    sentAt,
}: ChatMessageProps): JSX.Element {
    const isInbound = direction === "inbound";

    const formatTime = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString("es-AR", {
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <Row $isInbound={isInbound}>
            {!isInbound && (
                <Avatar>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <line x1="7" y1="2" x2="7" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        <line x1="2" y1="7" x2="12" y2="7" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </Avatar>
            )}
            <BubbleColumn $isInbound={isInbound}>
                <Bubble $isInbound={isInbound}>{formatBody(body)}</Bubble>
                <Time>{formatTime(sentAt)}</Time>
            </BubbleColumn>
        </Row>
    );
}
